import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import {
  getArenaMatchCards,
  getArenaMatchSnapshot,
  postArenaClaimSlot,
  postArenaForfeit,
  postArenaSelectCard,
} from "../../api/arenaApi"
import {
  connectBattleSocket,
  emitBattleEvent,
  emitBattleEventWhenReady,
  onBattleEvent,
} from "../../socket/battleSocket"
import {
  ArenaMatchEvent,
  ArenaMatchFinishedPayload,
  ArenaMatchFoundPayload,
  ArenaMatchPhase,
  ArenaMatchPlayer,
  ArenaMatchSnapshot,
  ArenaSlotNumber,
  ArenaTeamId,
  ArenaActiveDuel,
} from "../../types/arena"
import { BattleCardView } from "../../types/battle"
import { enrichBattleCardsShardsFromSource } from "../../utils/battleCards"
import {
  normalizeArenaActiveDuel,
  normalizeArenaMatchCards,
  normalizeArenaMatchEvent,
  normalizeArenaMatchFinished,
  normalizeArenaMatchFound,
  normalizeArenaMatchSnapshot,
} from "../../utils/arenaNormalize"
import { useAuth } from "../../context/AuthContext"
import { useGuardiansPassport } from "../passport/GuardiansPassportContext"

export type ArenaScreenPhase =
  | "lobby"
  | "queue"
  | "capacity_wait"
  | "slots"
  | "combat"
  | "result"

type MatchState = {
  matchId: string | null
  phase: ArenaMatchPhase
  lastSeq: number
  team: ArenaTeamId | null
  myUserId: number | null
  slotDeadlineAt: string | null
  players: ArenaMatchPlayer[]
  activeDuel: ArenaActiveDuel | null
  log: ArenaMatchEvent[]
  finished: ArenaMatchFinishedPayload | null
  yourCards: BattleCardView[]
  burnedCardIds: string[]
}

const EMPTY: MatchState = {
  matchId: null,
  phase: "LOBBY",
  lastSeq: 0,
  team: null,
  myUserId: null,
  slotDeadlineAt: null,
  players: [],
  activeDuel: null,
  log: [],
  finished: null,
  yourCards: [],
  burnedCardIds: [],
}

function applySnapshot(prev: MatchState, snap: ArenaMatchSnapshot): MatchState {
  return {
    matchId: snap.matchId,
    phase: snap.phase,
    lastSeq: Math.max(prev.lastSeq, snap.seq),
    team: snap.team ?? prev.team,
    myUserId: snap.myUserId ?? prev.myUserId,
    slotDeadlineAt: snap.slotDeadlineAt,
    players: snap.players.length ? snap.players : prev.players,
    activeDuel: snap.activeDuel,
    log: snap.log.length ? snap.log : prev.log,
    finished: snap.finished ?? prev.finished,
    yourCards: snap.yourCards.length ? snap.yourCards : prev.yourCards,
    burnedCardIds: snap.burnedCardIds.length
      ? snap.burnedCardIds
      : prev.burnedCardIds,
  }
}

function applyMatchFound(
  prev: MatchState,
  found: ArenaMatchFoundPayload,
  myUserId: number | null
): MatchState {
  return {
    ...prev,
    matchId: found.matchId,
    phase: "SLOT_PICK",
    team: found.team,
    myUserId: myUserId ?? prev.myUserId,
    slotDeadlineAt: found.slotDeadlineAt,
    players: found.players.length ? found.players : prev.players,
    finished: null,
    activeDuel: null,
  }
}

function mergePlayersFromPayload(
  players: ArenaMatchPlayer[],
  payload: Record<string, unknown>
): ArenaMatchPlayer[] {
  const userIdRaw = payload.userId ?? payload.user_id
  const userId =
    typeof userIdRaw === "number"
      ? userIdRaw
      : typeof userIdRaw === "string"
        ? Number(userIdRaw)
        : null
  if (userId == null || !Number.isFinite(userId)) return players

  const slotRaw = payload.slot
  const slot =
    slotRaw === 1 || slotRaw === 2 || slotRaw === 3
      ? slotRaw
      : typeof slotRaw === "string" && ["1", "2", "3"].includes(slotRaw)
        ? (Number(slotRaw) as ArenaSlotNumber)
        : null

  const stateRaw = String(payload.state ?? payload.status ?? "").toUpperCase()
  const state =
    stateRaw === "DEAD" || stateRaw === "ELIMINATED" ? stateRaw : null

  return players.map((p) => {
    if (p.userId !== userId) return p
    return {
      ...p,
      slot: slot ?? p.slot,
      state: (state as ArenaMatchPlayer["state"]) ?? p.state,
      nickname:
        typeof payload.nickname === "string" ? payload.nickname : p.nickname,
    }
  })
}

function applyEvent(prev: MatchState, event: ArenaMatchEvent): MatchState {
  if (prev.matchId && event.matchId !== prev.matchId) return prev
  if (event.seq > 0 && event.seq <= prev.lastSeq) return prev

  let next: MatchState = {
    ...prev,
    matchId: event.matchId || prev.matchId,
    phase: event.phase !== "LOBBY" ? event.phase : prev.phase,
    lastSeq: Math.max(prev.lastSeq, event.seq),
    log: [...prev.log, event].slice(-80),
  }

  const p = event.payload

  switch (event.eventType) {
    case "MATCH_FOUND": {
      const found = normalizeArenaMatchFound({ ...p, matchId: event.matchId })
      if (found) next = applyMatchFound(next, found, next.myUserId)
      break
    }
    case "SLOT_CLAIMED":
    case "SLOTS_AUTO_ASSIGNED":
      next.players = mergePlayersFromPayload(next.players, p)
      if (Array.isArray(p.players)) {
        const found = normalizeArenaMatchFound({
          matchId: event.matchId,
          team: next.team,
          slotDeadlineAt: next.slotDeadlineAt,
          players: p.players,
        })
        if (found?.players.length) next.players = found.players
      }
      break
    case "DUEL_STARTED":
      next.phase = "COMBAT"
      next.activeDuel = normalizeArenaActiveDuel(p) ?? next.activeDuel
      break
    case "CARD_SELECTED": {
      const cardId =
        typeof p.cardId === "string"
          ? p.cardId
          : typeof p.card_id === "string"
            ? p.card_id
            : null
      const uid = Number(p.userId ?? p.user_id)
      if (
        next.activeDuel &&
        Number.isFinite(uid) &&
        next.myUserId != null &&
        uid === next.myUserId &&
        cardId
      ) {
        next.activeDuel = { ...next.activeDuel, selectedCardId: cardId }
      }
      break
    }
    case "DUEL_RESOLVED": {
      const burned =
        typeof p.burnedCardId === "string"
          ? p.burnedCardId
          : typeof p.burned_card_id === "string"
            ? p.burned_card_id
            : typeof p.winnerCardId === "string"
              ? p.winnerCardId
              : null
      if (burned && !next.burnedCardIds.includes(burned)) {
        next.burnedCardIds = [...next.burnedCardIds, burned]
      }
      next.players = mergePlayersFromPayload(next.players, p)
      if (Array.isArray(p.players)) {
        const snap = normalizeArenaMatchSnapshot({
          matchId: event.matchId,
          players: p.players,
        })
        if (snap?.players.length) next.players = snap.players
      }
      next.activeDuel = null
      break
    }
    case "DUEL_TIMEOUT":
    case "PLAYER_FORFEIT":
    case "PLAYER_DISCONNECT_ELIMINATED":
      next.players = mergePlayersFromPayload(next.players, {
        ...p,
        state: "DEAD",
      })
      break
    case "MATCH_FINISHED": {
      const finished = normalizeArenaMatchFinished({
        ...p,
        matchId: event.matchId,
      })
      next.phase = "FINISHED"
      next.finished = finished
      next.activeDuel = null
      break
    }
    default:
      break
  }

  return next
}

function phaseFromMatch(state: MatchState, queueMode: "idle" | "queue" | "capacity"): ArenaScreenPhase {
  if (state.phase === "FINISHED" || state.finished) return "result"
  if (state.phase === "COMBAT" || state.activeDuel) return "combat"
  if (state.phase === "SLOT_PICK" || state.matchId) return "slots"
  if (queueMode === "capacity") return "capacity_wait"
  if (queueMode === "queue") return "queue"
  return "lobby"
}

export function useArenaMatch(options: {
  arenaId: string
  initialMatchId?: string | null
}) {
  const { token } = useAuth()
  const { passport, refreshPassport, collectionCards } = useGuardiansPassport()
  const collectionRef = useRef(collectionCards)
  collectionRef.current = collectionCards

  const enrichCards = useCallback((cards: BattleCardView[]) => {
    return enrichBattleCardsShardsFromSource(cards, collectionRef.current)
  }, [])

  const [match, setMatch] = useState<MatchState>(() => ({
    ...EMPTY,
    matchId: options.initialMatchId ?? null,
    myUserId: passport?.user_id ?? null,
    phase: options.initialMatchId ? "SLOT_PICK" : "LOBBY",
  }))
  const [queueMode, setQueueMode] = useState<"idle" | "queue" | "capacity">("idle")
  const [queueSize, setQueueSize] = useState(0)
  const [needed, setNeeded] = useState(6)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const lastSeqRef = useRef(0)
  const matchIdRef = useRef(match.matchId)

  matchIdRef.current = match.matchId
  lastSeqRef.current = match.lastSeq

  const screenPhase = useMemo(
    () => phaseFromMatch(match, queueMode),
    [match, queueMode]
  )

  const requestSnapshot = useCallback(
    async (matchId: string) => {
      emitBattleEvent("arena-match-snapshot", { matchId })
      try {
        const snap = await getArenaMatchSnapshot(matchId)
        if (snap) {
          setMatch((prev) => {
            const next = applySnapshot(prev, snap)
            return {
              ...next,
              yourCards: enrichCards(next.yourCards),
            }
          })
          lastSeqRef.current = Math.max(lastSeqRef.current, snap.seq)
        }
      } catch {
        /* WS snapshot preferred */
      }
      try {
        const cards = await getArenaMatchCards(matchId)
        if (cards) {
          setMatch((prev) => ({
            ...prev,
            yourCards: cards.yourCards.length
              ? enrichCards(cards.yourCards)
              : prev.yourCards,
            burnedCardIds: cards.burnedCardIds.length
              ? cards.burnedCardIds
              : prev.burnedCardIds,
          }))
        }
      } catch {
        emitBattleEvent("arena-my-cards", { matchId })
      }
    },
    [enrichCards]
  )

  useEffect(() => {
    if (passport?.user_id != null) {
      setMatch((prev) =>
        prev.myUserId === passport.user_id
          ? prev
          : { ...prev, myUserId: passport.user_id }
      )
    }
  }, [passport?.user_id])

  useEffect(() => {
    let cancelled = false
    const cleanups: (() => void)[] = []

    const setup = async () => {
      try {
        await connectBattleSocket(token)
      } catch {
        if (!cancelled) setError("Нет соединения с ареной")
        return
      }
      if (cancelled) return

      cleanups.push(
        onBattleEvent("arena-match-found", (raw) => {
          const found = normalizeArenaMatchFound(raw)
          if (!found) return
          setQueueMode("idle")
          setMatch((prev) =>
            applyMatchFound(prev, found, passport?.user_id ?? prev.myUserId)
          )
          void requestSnapshot(found.matchId)
        })
      )

      cleanups.push(
        onBattleEvent("arena-match-event", (raw) => {
          const event = normalizeArenaMatchEvent(raw)
          if (!event) return
          if (event.seq > 0 && event.seq <= lastSeqRef.current) return
          setMatch((prev) => applyEvent(prev, event))
        })
      )

      cleanups.push(
        onBattleEvent("arena-match-snapshot", (raw) => {
          const snap = normalizeArenaMatchSnapshot(raw)
          if (!snap) return
          setMatch((prev) => applySnapshot(prev, snap))
        })
      )

      cleanups.push(
        onBattleEvent("arena-match-finished", (raw) => {
          const finished = normalizeArenaMatchFinished(raw)
          if (!finished) return
          setMatch((prev) => ({
            ...prev,
            matchId: finished.matchId,
            phase: "FINISHED",
            finished,
            activeDuel: null,
          }))
          void refreshPassport()
        })
      )

      cleanups.push(
        onBattleEvent("arena-my-cards", (raw) => {
          const cards = normalizeArenaMatchCards(raw)
          if (!cards) return
          if (matchIdRef.current && cards.matchId !== matchIdRef.current) return
          setMatch((prev) => ({
            ...prev,
            yourCards: enrichCards(cards.yourCards),
            burnedCardIds: cards.burnedCardIds,
          }))
        })
      )

      cleanups.push(
        onBattleEvent("arena-queued", (raw) => {
          setQueueMode("queue")
          setQueueSize(raw.queueSize)
          setNeeded(raw.needed || 6)
        })
      )

      cleanups.push(
        onBattleEvent("arena-queue-cancelled", () => {
          setQueueMode("idle")
          setQueueSize(0)
        })
      )

      cleanups.push(
        onBattleEvent("arena-capacity-wait", (raw) => {
          setQueueMode("capacity")
          if (raw.queueSize != null) setQueueSize(raw.queueSize)
          if (raw.needed != null) setNeeded(raw.needed)
        })
      )

      if (options.initialMatchId) {
        void requestSnapshot(options.initialMatchId)
      }
    }

    void setup()

    return () => {
      cancelled = true
      cleanups.forEach((fn) => fn())
    }
  }, [
    token,
    options.initialMatchId,
    passport?.user_id,
    requestSnapshot,
    refreshPassport,
  ])

  const claimSlot = useCallback(
    async (slot: ArenaSlotNumber) => {
      const matchId = matchIdRef.current
      if (!matchId || busy) return
      setBusy(true)
      setError(null)
      try {
        const ok = await emitBattleEventWhenReady(token, "arena-claim-slot", {
          matchId,
          slot,
        })
        if (!ok) {
          await postArenaClaimSlot({ matchId, slot })
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : "Ошибка слота")
      } finally {
        setBusy(false)
      }
    },
    [busy, token]
  )

  const selectCard = useCallback(
    async (cardId: string, abilityId?: string | null) => {
      const matchId = matchIdRef.current
      if (!matchId || busy) return
      setBusy(true)
      setError(null)
      try {
        const payload = {
          matchId,
          cardId,
          ...(abilityId ? { abilityId } : {}),
        }
        const ok = await emitBattleEventWhenReady(
          token,
          "arena-select-card",
          payload
        )
        if (!ok) {
          await postArenaSelectCard(payload)
        }
        setMatch((prev) =>
          prev.activeDuel
            ? {
                ...prev,
                activeDuel: { ...prev.activeDuel, selectedCardId: cardId },
              }
            : prev
        )
      } catch (e) {
        setError(e instanceof Error ? e.message : "Ошибка выбора карты")
      } finally {
        setBusy(false)
      }
    },
    [busy, token]
  )

  const forfeit = useCallback(async () => {
    const matchId = matchIdRef.current
    if (!matchId || busy) return
    setBusy(true)
    setError(null)
    try {
      const ok = await emitBattleEventWhenReady(token, "arena-forfeit", {
        matchId,
      })
      if (!ok) {
        await postArenaForfeit(matchId)
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Не удалось сдаться")
    } finally {
      setBusy(false)
    }
  }, [busy, token])

  const setQueued = useCallback((size: number, need: number) => {
    setQueueMode("queue")
    setQueueSize(size)
    setNeeded(need)
  }, [])

  const setCapacityWait = useCallback(() => {
    setQueueMode("capacity")
  }, [])

  const clearQueue = useCallback(() => {
    setQueueMode("idle")
    setQueueSize(0)
  }, [])

  const resetToLobby = useCallback(() => {
    setMatch({
      ...EMPTY,
      myUserId: passport?.user_id ?? null,
    })
    setQueueMode("idle")
    setQueueSize(0)
    setError(null)
  }, [passport?.user_id])

  const availableCards = useMemo(
    () => match.yourCards.filter((c) => !match.burnedCardIds.includes(c.id)),
    [match.yourCards, match.burnedCardIds]
  )

  const myPlayer = useMemo(
    () =>
      match.players.find(
        (p) =>
          (match.myUserId != null && p.userId === match.myUserId) ||
          (passport?.user_id != null && p.userId === passport.user_id)
      ) ?? null,
    [match.players, match.myUserId, passport?.user_id]
  )

  const inActiveDuel = useMemo(() => {
    if (!match.activeDuel || match.myUserId == null) return false
    return (
      match.activeDuel.teamAUserId === match.myUserId ||
      match.activeDuel.teamBUserId === match.myUserId
    )
  }, [match.activeDuel, match.myUserId])

  return {
    screenPhase,
    match,
    queueSize,
    needed,
    busy,
    error,
    setError,
    availableCards,
    myPlayer,
    inActiveDuel,
    claimSlot,
    selectCard,
    forfeit,
    requestSnapshot,
    setQueued,
    setCapacityWait,
    clearQueue,
    resetToLobby,
  }
}
